<template>
	<v-container class="pb-3xl">
		<v-storyblok-articles-nav
			class="mx-1 mb-6 pt-2"
			:article-landers="articleLanders"
		/>

		<v-storyblok-articles-top-categories
			class="mb-10"
			:category-entries="categoryEntries"
		/>

		<div class="mb-8 flex items-center">
			<v-h3 tag="h1">
				<!-- Different titles for different scenarios -->
				{{
					categoryOverrideTitle
						? categoryOverrideTitle
						: searchTerm
							? t("articles")
							: story.content.title
				}}
			</v-h3>

			<!-- Users might not be able to find how to undo a "tag" (category) selection otherwise -->
			<nuxt-link
				v-if="categoryOverride"
				class="ml-5 text-4xl text-gray-600 outline-hidden"
				:to="route.path"
				:title="t('back')"
			>
				<span class="sr-only">{{ t("back") }}</span>

				<v-icon icon="close" />
			</nuxt-link>
		</div>

		<client-only>
			<suspense>
				<div>
					<form class="mb-10 flex items-center gap-2" @submit.prevent>
						<label for="search-articles" class="sr-only">
							{{ searchPromptText }}
						</label>

						<v-input
							id="search-articles"
							v-model="searchValue"
							class="max-w-100 grow"
							:placeholder="searchPromptText"
							type="search"
							clearable
							@clear="searchValue = ''"
						/>
					</form>

					<div class="space-y-6 md:space-y-9">
						<v-storyblok-articles-pinned
							v-show="!searchTerm"
							:categories="landerCategories"
						/>

						<v-storyblok-article-feed
							:categories="landerCategories"
							:search-term="searchTerm"
						/>
					</div>
				</div>

				<template #fallback>
					{{ t("loading-articles") }}
				</template>
			</suspense>

			<template #placeholder>
				<div class="mt-10 flex flex-col items-center justify-center">
					<v-spinner />

					<p>{{ t("loading-articles") }}</p>
				</div>
			</template>
		</client-only>
	</v-container>
</template>

<script setup lang="ts">
import {watchDebounced} from "@vueuse/core";
import type {ISbStoryData} from "storyblok-js-client";
import type {SbDatasourceEntry} from "@pkg/shared/types";

import {useLocalization} from "@pkg/shared/composables/use-localization";
import {useStoryblokArticles} from "@pkg/shared/composables/use-storyblok-articles";
import {useStoryblokBetaGet} from "@pkg/shared/composables/use-storyblok-beta-get";
import {useStoryblokStoriesList} from "@pkg/shared/composables/use-storyblok-stories-list";

const props = defineProps<{
	story: ISbStoryData;
}>();

const {
	public: {appUrl},
} = useRuntimeConfig();

const route = useRoute();
const router = useRouter();

const absoluteUrl = new URL(route.path, appUrl).href;

const {languageTag} = useLocalization();
const {t, locale} = useI18n();

const articlesSlug = t("storyblok.articles");

// Updated in a watcher
const searchValue = ref(route.query.query?.toString() ?? "");
// Mirrors search value but only updates on URL query change
const searchTerm = ref(searchValue.value);

// A category being selected in the query overrides any lander page's possible categories
const categoryOverride = computed(() => route.query.category?.toString());

const landerCategories = computed<string[]>(() => {
	if (categoryOverride.value) {
		return [categoryOverride.value];
	}
	if (
		props.story.content.article_categories &&
		props.story.content.article_categories.length > 0
	) {
		return props.story.content.article_categories;
	}
	// No limit, ie show all articles if no categories are selected
	return [];
});

// Await, loading the landers menu after mount would look a bit funny
const {data: landerStoriesRes} = await useStoryblokStoriesList(
	"article-lander-stories",
	{
		starts_with: articlesSlug,
		content_type: "sb-articles-lander",
	},
);

const articleLanders = computed(() =>
	landerStoriesRes.value.sort(
		(a, b) => (b.content.priority ?? 0) - (a.content.priority ?? 0),
	),
);

// NOTE: The categories are intentionally fetched from BETA space as this prevents us from having to duplicate the list
// in every environment
const {data: categoryEntriesRes} = useStoryblokBetaGet(
	"article-categories",
	"datasource_entries",
	{
		datasource: "article-categories",

		per_page: 100,
	},
);

const categoryEntries = computed<SbDatasourceEntry[]>(() => {
	if (!categoryEntriesRes.value) {
		return [];
	}
	const allEntries: SbDatasourceEntry[] =
		categoryEntriesRes.value.data.datasource_entries;
	const localePrefix = `${locale.value}:`;
	const localeEntries = allEntries.filter((x) =>
		x.value.startsWith(localePrefix),
	);

	if (articleLanders.value.length === 0) {
		return localeEntries;
	}
	// Leave out categories that already have matching folders set up for them
	return localeEntries.filter((entry) =>
		articleLanders.value.every((lander) => lander.name !== entry.name),
	);
});

const currentCategoryName = computed(() => {
	if (!categoryOverride.value) {
		return undefined;
	}
	const matchingEntry = categoryEntries.value.find(
		(x) => x.value === categoryOverride.value,
	);
	if (!matchingEntry) {
		return undefined;
	}
	return matchingEntry.name;
});

const categoryOverrideTitle = computed(() => {
	if (!currentCategoryName.value) {
		return undefined;
	}
	return currentCategoryName.value;
});

const searchPromptText = computed(() => {
	if (currentCategoryName.value) {
		return t("search-prompt-for-category", {
			category: currentCategoryName.value,
		});
	}

	return t("search-prompt");
});

// Await because this is needed in the schema during SSR
const {data: latestArticlesResponse} = await useStoryblokArticles({
	categories: landerCategories,
	perPage: 3,
	page: 1,
});

watchDebounced(
	searchValue,
	(val) => {
		searchTerm.value = val;
		router.replace({query: {...route.query, query: val || undefined}});
	},
	{debounce: 400},
);

watch(categoryOverride, () => {
	searchValue.value = "";
	searchTerm.value = "";
});

useSeoMeta({
	title:
		props.story.content.meta?.title ||
		props.story.content.title ||
		props.story.name,

	description:
		(props.story.content.meta?.description ||
			props.story.content?.short_description) ??
		undefined,
	ogTitle: props.story.content.meta?.title || props.story.content.title,

	ogDescription:
		(props.story.content.meta?.description ||
			props.story.content?.short_description) ??
		undefined,
	ogImage: props.story.content?.og_image?.filename ?? undefined,
});

const getSchemaIdentifier = (type: string, url?: string) =>
	`${url || absoluteUrl}#${type}`;

useJsonld({
	"@context": "https://schema.org",
	"@graph": [
		{
			"@type": "WebPage",
			"@id": getSchemaIdentifier("webpage"),
			url: absoluteUrl,
			name: props.story.content?.title || "Blog",
			inLanguage: languageTag,
		},
		{
			"@type": "Blog",
			"@id": getSchemaIdentifier("blog"),
			name: props.story.content?.title || "Blog",
			url: absoluteUrl,
			inLanguage: languageTag,
		},
		{
			"@type": "ItemList",
			"@id": getSchemaIdentifier("list"),
			name: "Latest articles",
			itemListElement: (latestArticlesResponse.value?.stories || []).map(
				(article, index) => ({
					"@type": "ListItem",
					position: index,
					item: {
						"@type": "BlogPosting",
						"@id": getSchemaIdentifier(
							"article",
							new URL(article.full_slug, appUrl).href,
						),
						headline: article.name,
						url: new URL(article.full_slug, appUrl).href,
						inLanguage: languageTag,
					},
				}),
			),
		},
	],
});
</script>

<i18n lang="json">
{
	"et": {
		"loading-articles": "Artikleid laaditakse...",
		"articles": "Artiklid",
		"search-prompt": "Otsi artikleid",
		"search-prompt-for-category": "Otsi kategooriast: {category}",
		"search": "Otsi",
		"back": "Tagasi"
	},
	"fi": {
		"loading-articles": "Ladataan artikkeleita...",
		"articles": "Artikkelit",
		"search-prompt": "Hae artikkeleita",
		"search-prompt-for-category": "Hae kategoriasta: {category}",
		"search": "Hae",
		"back": "Takaisin"
	}
}
</i18n>
